import React, { useEffect } from "react";
import styled from "styled-components/macro";
import { match } from "ts-pattern";
import { ErrorIcon, WarningIcon } from "../Icons/Icons";
import {
  DesktopBar,
  DesktopErrorIconArea,
  DesktopErrorMessage,
  DesktopWarningIconArea,
  DesktopWarningMessage,
} from "../Notifications/Notifications";
import { Citations } from "./Citations";
import { MessageContent } from "./MessageContent";
import { MessageFeedback } from "./MessageFeedback";
import { PendingAssistantBubble } from "./PendingAssistantBubble";
import type { ChatMessage, Feedback } from "./types";

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Message = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_lg")} 14px;
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  background-color: #f6f7f9;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  max-width: 92%;
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: 21px;
`;

const UserMessage = styled(Message)`
  align-self: flex-end;
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_brand_mixed")};
  border-color: ${({ theme }) => theme.brandColor};
  white-space: pre-wrap;
  word-break: break-word;
`;

const AIMessage = styled(Message)`
  align-self: flex-start;
  background-color: white;
  width: 92%;
  && p:first-child {
    margin-block-start: 0;
    margin-top: 0;
  }
  && p:last-child {
    margin-block-end: 0;
    margin-bottom: 0;
  }
`;

// Inline-render the same notification visuals the global toast uses, so a
// chat error matches the look of a notifyError toast (without absolute
// positioning). Layout-only extension; colors/typography come from the
// shared notification components.
const InlineNotificationBar = styled(DesktopBar)`
  align-self: center;
  max-width: 92%;
`;

export const MessageTimeline = ({
  messages,
  tenantId,
  chatBotName,
  contentRef,
  onSetFeedback,
}: {
  messages: ChatMessage[];
  tenantId?: string;
  chatBotName?: string;
  contentRef: React.RefObject<HTMLDivElement>;
  onSetFeedback?: (clientMessageId: string, next: Feedback | null) => void;
}) => {
  useEffect(() => {
    if (contentRef?.current && messages) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [contentRef, messages]);

  return (
    <TimelineContainer>
      {messages.map((message) =>
        match(message.role)
          .with("user", () => (
            <UserMessage key={message.id}>{message.content}</UserMessage>
          ))
          .with("assistant", () =>
            message.pending ? (
              <PendingAssistantBubble key={message.id} />
            ) : (
              <AIMessage key={message.id}>
                <MessageContent
                  message={message}
                  tenantId={tenantId}
                  chatBotName={chatBotName}
                />
                {message.citations && message.citations.length > 0 && (
                  <Citations citations={message.citations} />
                )}
                {onSetFeedback && message.server_message_id && (
                  <MessageFeedback
                    feedback={message.feedback}
                    onChange={(next) => onSetFeedback(message.id, next)}
                  />
                )}
              </AIMessage>
            )
          )
          .with("error", () =>
            message.error_kind === "upstream" ? (
              <InlineNotificationBar
                key={message.id}
                data-error-kind="upstream"
              >
                <DesktopWarningIconArea>
                  <WarningIcon fill="#ffffff" />
                </DesktopWarningIconArea>
                <DesktopWarningMessage>{message.content}</DesktopWarningMessage>
              </InlineNotificationBar>
            ) : (
              <InlineNotificationBar key={message.id} data-error-kind="fatal">
                <DesktopErrorIconArea>
                  <ErrorIcon fill="#ffffff" />
                </DesktopErrorIconArea>
                <DesktopErrorMessage>{message.content}</DesktopErrorMessage>
              </InlineNotificationBar>
            )
          )
          .with("info", () => (
            <Message
              key={message.id}
              role="status"
              style={{ alignSelf: "center", textAlign: "center" }}
            >
              {message.content}
            </Message>
          ))
          .exhaustive()
      )}
    </TimelineContainer>
  );
};
